import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  CalendarDays,
  Check,
  CheckCircle2,
  Hourglass,
  Link2,
  PlusCircle,
  QrCode,
  RotateCw,
  Search,
  Store,
  TrendingUp,
  Wallet,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatCurrency, formatElapsed } from "@/lib/format";
import { useAuth } from "@/hooks/useAuth";
import pixService from "@/services/pixService";
import { showError, showInfo } from "@/store/errorStore";
import PixPaymentCard, { getPixTitle } from "./PixPaymentCard";
import PaymentDetailsModal from "./PaymentDetailsModal";
import PixQrCodeModal from "./PixQrCodeModal";
import PixReceivedModal from "./PixReceivedModal";
import PixFormModal from "./PixFormModal";

// Cobranças exibidas antes do "Ver todos"
const PAGE_SIZE = 12;
// Atualização silenciosa (pagamentos confirmados em outras telas)
const REFRESH_MS = 30000;

const FILTERS = {
  all: { label: "Todos", match: () => true },
  pending: { label: "Pendentes", match: (e) => !e.paid },
  paid: { label: "Pagos", match: (e) => e.paid },
};

/** Minúsculas e sem acento, para a busca */
const normalize = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const isSameDay = (a, b) =>
  a &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** Erros HTTP já abrem o modal global; aqui só falhas locais (validação, rede sem resposta) */
const showLocalError = (error, fallback) => {
  if (!error?.response && !error?.request) {
    showError(error?.message || fallback, "Pagamentos");
  }
};

function MetricCard({ label, icon: Icon, iconClass, onClick, active, children }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      aria-pressed={onClick ? active : undefined}
      className={cn(
        "flex min-w-0 flex-col justify-between rounded-xl border bg-card p-3.5 text-left shadow-soft transition-all",
        onClick && "hover:shadow-ambient active:scale-[0.99]",
        active && "border-primary ring-2 ring-primary/15"
      )}
    >
      <span className="flex w-full items-center justify-between gap-2">
        <span className="truncate text-xs font-semibold text-muted-foreground">{label}</span>
        <span
          className={cn(
            "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full",
            iconClass
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
      </span>
      <span className="mt-2 block min-w-0">{children}</span>
    </Tag>
  );
}

function FilterChip({ active, onClick, count, icon: Icon, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-semibold shadow-sm transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "bg-card text-muted-foreground hover:text-foreground"
      )}
    >
      {Icon && <Icon className="h-4 w-4" />}
      {children}
      {count !== undefined && (
        <span
          className={cn(
            "flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px]",
            active ? "bg-white/20" : "bg-surface-container"
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function ShortcutButton({ icon: Icon, title, hint, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex min-w-0 flex-col items-start rounded-xl border bg-card p-3 text-left shadow-soft transition-all hover:shadow-ambient active:scale-[0.98] disabled:pointer-events-none disabled:opacity-55"
    >
      <Icon className="mb-1 h-5 w-5 text-primary" />
      <span className="text-[13px] font-semibold text-foreground">{title}</span>
      <span className="w-full truncate text-[11px] text-muted-foreground">{hint}</span>
    </button>
  );
}

function ListSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2" aria-hidden="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-3 rounded-xl border bg-card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-24 rounded-full" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
            <Skeleton className="h-5 w-20" />
          </div>
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
          <div className="grid grid-cols-2 gap-2">
            <Skeleton className="h-10 rounded-full" />
            <Skeleton className="h-10 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Pagamentos gerados (docs/tela/code.html): métricas, busca, filtros
 * rápidos, cards por cobrança e atalhos de cobrança no caixa.
 */
export default function PixManagement() {
  const { company, companyName } = useAuth();

  const [pixList, setPixList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [now, setNow] = useState(() => new Date());

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [onlyToday, setOnlyToday] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // Modais
  const [detailsPix, setDetailsPix] = useState(null);
  const [qrPix, setQrPix] = useState(null);
  const [loadingQrTxid, setLoadingQrTxid] = useState(null);
  const [receivingPix, setReceivingPix] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [pixToDelete, setPixToDelete] = useState(null);
  const [deletingTxid, setDeletingTxid] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);

  const loadData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await pixService.getAllPix(silent ? { silent: true } : undefined);
      setPixList(Array.isArray(data) ? data : []);
      setLoadError(false);
    } catch (error) {
      if (!silent) {
        showLocalError(error, "Erro ao carregar os pagamentos");
        setLoadError(true);
      }
    } finally {
      if (!silent) setLoading(false);
      setNow(new Date());
    }
  }, []);

  useEffect(() => {
    loadData();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") loadData({ silent: true });
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadData]);

  useEffect(() => {
    if (!keyCopied) return undefined;
    const timer = setTimeout(() => setKeyCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [keyCopied]);

  // Status, valor, data e texto de busca calculados uma vez por carga
  const entries = useMemo(
    () =>
      pixList
        .map((pix) => {
          const created = pixService.parseDate(pix.createdAt);
          return {
            pix,
            paid: pixService.isPaid(pix),
            amount: pixService.getAmount(pix),
            received: pixService.getReceivedAmount(pix),
            created,
            search: normalize(
              [
                pix.orderCode,
                pix.name,
                pix.txid,
                pix.contact,
                String(pix.contact || "").replace(/\D/g, ""),
                pix.pay_method,
                pixService.getAmount(pix).toFixed(2).replace(".", ","),
              ].join(" ")
            ),
          };
        })
        .sort((a, b) => (b.created?.getTime() || 0) - (a.created?.getTime() || 0)),
    [pixList]
  );

  const stats = useMemo(() => {
    const s = { total: 0, received: 0, paid: 0, paidAmount: 0, pending: 0, oldestPending: null };
    entries.forEach((e) => {
      s.total += e.amount;
      if (e.paid) {
        s.paid += 1;
        s.received += e.received;
        s.paidAmount += e.amount;
      } else {
        s.pending += 1;
        if (e.created && (!s.oldestPending || e.created < s.oldestPending)) {
          s.oldestPending = e.created;
        }
      }
    });
    return s;
  }, [entries]);

  // Busca + "Hoje": base das contagens dos chips e da lista
  const scoped = useMemo(() => {
    const term = normalize(searchQuery).trim();
    return entries.filter(
      (e) => (!term || e.search.includes(term)) && (!onlyToday || isSameDay(e.created, now))
    );
  }, [entries, searchQuery, onlyToday, now]);

  const counts = useMemo(() => {
    const c = {};
    Object.entries(FILTERS).forEach(([key, f]) => {
      c[key] = scoped.filter(f.match).length;
    });
    return c;
  }, [scoped]);

  const visible = useMemo(
    () => scoped.filter(FILTERS[statusFilter]?.match || FILTERS.all.match),
    [scoped, statusFilter]
  );

  useEffect(() => {
    setShowAll(false);
  }, [searchQuery, statusFilter, onlyToday]);

  const shown = showAll ? visible : visible.slice(0, PAGE_SIZE);
  const hasFilters = Boolean(searchQuery.trim()) || statusFilter !== "all" || onlyToday;
  const total = pixList.length;
  const conversion = total ? Math.round((stats.paid / total) * 100) : 0;
  const confirmedRate = stats.paidAmount
    ? Math.min(100, Math.round((stats.received / stats.paidAmount) * 100))
    : 0;

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setOnlyToday(false);
  };

  const toggleStatus = (key) => setStatusFilter((current) => (current === key ? "all" : key));

  // Dados PIX da empresa para os atalhos do caixa
  const pixConfigured = pixService.isCompanyPixConfigured(company);
  const pixKey = String(company?.pixKey || "").trim();
  const shortcutHint = !company
    ? "Selecione uma empresa"
    : !pixConfigured
    ? "Configure o PIX em Empresas"
    : null;

  /* ---------- Ações ---------- */

  const handleViewQRCode = async (pix) => {
    if (!pix?.txid) return;
    setLoadingQrTxid(pix.txid);
    try {
      // Busca o registro atualizado (payload/qrcode podem ter sido regenerados)
      const fresh = await pixService.getPixByTxid(pix.txid);
      setQrPix({ ...pix, ...(fresh || {}) });
    } catch (error) {
      showLocalError(error, "Erro ao carregar o QR Code");
    } finally {
      setLoadingQrTxid(null);
    }
  };

  const handleConfirmReceived = async (receivedData) => {
    const pix = receivingPix;
    if (!pix) return;
    setConfirming(true);
    try {
      if (receivedData) {
        await pixService.markAsReceived(pix.txid, receivedData);
      } else {
        await pixService.confirmPix(pix.txid);
      }
      showInfo(
        `Pagamento ${getPixTitle(pix)} de ${formatCurrency(pixService.getAmount(pix))} confirmado`,
        "Pagamento recebido"
      );
      setReceivingPix(null);
      await loadData();
    } catch (error) {
      showLocalError(error, "Erro ao confirmar o recebimento");
    } finally {
      setConfirming(false);
    }
  };

  const handleCreatePix = async (pixData) => {
    setCreating(true);
    try {
      const created = await pixService.createPix(pixData);
      setShowCreateModal(false);
      await loadData();
      if (created?.payload || created?.qrcodeUrl) {
        // Mostra o QR recém-gerado já pronto para o cliente
        setQrPix({ ...pixData, orderCode: pixData.order_code, status: "N", ...created });
      } else {
        showInfo(`Cobrança de ${formatCurrency(pixData.amount)} registrada`, "Pagamentos");
      }
    } catch (error) {
      showLocalError(error, "Erro ao gerar o PIX");
    } finally {
      setCreating(false);
    }
  };

  const handleConfirmDelete = async () => {
    const pix = pixToDelete;
    setPixToDelete(null);
    if (!pix?.txid) return;
    setDeletingTxid(pix.txid);
    try {
      await pixService.deletePix(pix.txid);
      showInfo(`Cobrança ${getPixTitle(pix)} excluída`, "Pagamentos");
      await loadData();
    } catch (error) {
      showLocalError(error, "Erro ao excluir a cobrança");
    } finally {
      setDeletingTxid(null);
    }
  };

  const handleWhatsApp = (pix) => {
    const number = pixService.getWhatsAppNumber(pix.contact);
    if (!number) return;
    const firstName = String(pix.name || "").trim().split(/\s+/)[0];
    const message = [
      `Olá${firstName ? `, ${firstName}` : ""}!`,
      `Confirmamos o recebimento do seu pagamento de ${formatCurrency(
        pixService.getReceivedAmount(pix) || pixService.getAmount(pix)
      )}${pix.orderCode ? ` referente ao pedido #${pix.orderCode}` : ""}.`,
      pix.txid ? `Comprovante (ID da transação): ${pix.txid}` : "",
      `Obrigado pela preferência${companyName ? ` — ${companyName}` : ""}!`,
    ]
      .filter(Boolean)
      .join("\n");
    window.open(
      `https://wa.me/${number}?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleCounterQr = () => {
    const payload = pixService.buildStaticPayload(company);
    if (!payload) return;
    setQrPix({
      isStatic: true,
      payload,
      pixKey,
      name: company.pixMerchantName || company.name || companyName,
    });
  };

  const handleCopyKey = async () => {
    try {
      await pixService.copyToClipboard(pixKey);
      setKeyCopied(true);
    } catch (error) {
      showError("Não foi possível copiar a chave PIX.", "Pagamentos");
    }
  };

  const pendingHint = stats.pending
    ? stats.oldestPending
      ? `Mais antigo ${formatElapsed(stats.oldestPending, now)}`
      : "Aguardando pagamento"
    : "Nenhum em aberto";

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      {/* Título e ação principal */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="truncate font-display text-xl font-bold tracking-tight text-primary md:text-2xl">
              Pagamentos
            </h1>
            {!(loading && total === 0) && (
              <span className="flex-shrink-0 rounded-full bg-surface-container px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                {plural(total, "cobrança", "cobranças")}
              </span>
            )}
          </div>
          <p className="truncate text-xs text-muted-foreground md:text-sm">
            Controle de cobranças, PIX e fluxo financeiro
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="flex h-10 flex-shrink-0 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95"
        >
          <PlusCircle className="h-4 w-4" />
          Novo PIX
        </button>
      </div>

      {/* Métricas (2x2 no celular, 4 colunas no desktop) */}
      {loading && total === 0 ? (
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4" aria-hidden="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[104px] rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          <MetricCard label="Valor Total" icon={Wallet} iconClass="bg-brand-pistachio/30 text-primary">
            <span className="block truncate font-display text-lg font-bold tracking-tight text-primary md:text-xl">
              {formatCurrency(stats.total)}
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              Recebido: {formatCurrency(stats.received)}
            </span>
          </MetricCard>
          <MetricCard
            label="Recebidos"
            icon={CheckCircle2}
            iconClass="bg-secondary text-primary"
            onClick={() => toggleStatus("paid")}
            active={statusFilter === "paid"}
          >
            <span className="block font-display text-lg font-bold tracking-tight text-primary md:text-xl">
              {plural(stats.paid, "pago", "pagos")}
            </span>
            <span className="flex items-center gap-1 text-xs font-medium text-secondary-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-pistachio" />
              {stats.paid ? `${confirmedRate}% do valor confirmado` : "Nenhum ainda"}
            </span>
          </MetricCard>
          <MetricCard
            label="Pendentes"
            icon={Hourglass}
            iconClass="bg-terra-soft text-terra"
            onClick={() => toggleStatus("pending")}
            active={statusFilter === "pending"}
          >
            <span className="block font-display text-lg font-bold tracking-tight text-terra md:text-xl">
              {stats.pending} aguardando
            </span>
            <span className="block truncate text-xs text-muted-foreground">{pendingHint}</span>
          </MetricCard>
          <MetricCard label="Conversão" icon={TrendingUp} iconClass="bg-surface-container text-primary">
            <span className="block font-display text-lg font-bold tracking-tight text-primary md:text-xl">
              {conversion}% taxa
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              {stats.paid} de {plural(total, "cobrança", "cobranças")} pagas
            </span>
          </MetricCard>
        </div>
      )}

      {/* Busca */}
      <div className="relative flex items-center">
        <Search className="pointer-events-none absolute left-3.5 h-4 w-4 text-muted-foreground" />
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar por pedido, cliente ou ID..."
          aria-label="Buscar pagamentos"
          className="h-11 w-full rounded-xl border border-border bg-card pl-10 pr-10 text-sm text-foreground shadow-sm transition-colors placeholder:text-muted-foreground/80 focus:border-primary focus:outline-none focus:ring-[3px] focus:ring-brand-pistachio/25"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            aria-label="Limpar busca"
            className="absolute right-2 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Chips de filtro (scroll horizontal no celular) */}
      <div
        className="no-scrollbar -mx-4 flex items-center gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0"
        role="group"
        aria-label="Filtrar pagamentos"
      >
        {Object.entries(FILTERS).map(([key, f]) => (
          <FilterChip
            key={key}
            active={statusFilter === key}
            count={counts[key] || 0}
            onClick={() => setStatusFilter(key)}
          >
            {f.label}
          </FilterChip>
        ))}
        <FilterChip active={onlyToday} icon={CalendarDays} onClick={() => setOnlyToday((v) => !v)}>
          Hoje
        </FilterChip>
      </div>

      {/* Lista de cobranças */}
      {loading && total === 0 ? (
        <ListSkeleton />
      ) : loadError && total === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-12 text-center">
          <Wallet className="mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="font-display text-base font-semibold text-foreground">
            Não foi possível carregar os pagamentos
          </p>
          <button
            type="button"
            onClick={() => loadData()}
            className="mt-4 flex h-9 items-center gap-1 rounded-full border border-border bg-surface px-3.5 text-sm font-semibold text-primary transition-colors hover:bg-muted"
          >
            <RotateCw className="h-4 w-4" />
            Tentar novamente
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-12 text-center">
          <QrCode className="mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="font-display text-base font-semibold text-foreground">
            {hasFilters ? "Nenhuma cobrança encontrada" : "Nenhuma cobrança gerada"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {hasFilters
              ? "Tente outra busca ou limpe os filtros."
              : "Gere um PIX para cobrar um pedido ou uma venda no balcão."}
          </p>
          <button
            type="button"
            onClick={hasFilters ? clearFilters : () => setShowCreateModal(true)}
            className={cn(
              "mt-4 flex h-9 items-center gap-1 rounded-full px-3.5 text-sm font-semibold transition-colors",
              hasFilters
                ? "border border-border bg-surface text-primary hover:bg-muted"
                : "bg-primary text-primary-foreground hover:bg-brand-dark"
            )}
          >
            {hasFilters ? <X className="h-4 w-4" /> : <PlusCircle className="h-4 w-4" />}
            {hasFilters ? "Limpar filtros" : "Novo PIX"}
          </button>
        </div>
      ) : (
        <>
          <div
            className={cn(
              "grid grid-cols-1 gap-3 transition-opacity lg:grid-cols-2",
              loading && "opacity-60"
            )}
          >
            {shown.map(({ pix }, index) => (
              <div
                key={pix.txid || `${pix.orderCode}-${index}`}
                className={cn(deletingTxid && deletingTxid === pix.txid && "pointer-events-none opacity-50")}
              >
                <PixPaymentCard
                  pix={pix}
                  loadingQr={loadingQrTxid === pix.txid}
                  onOpenDetails={setDetailsPix}
                  onViewQRCode={handleViewQRCode}
                  onMarkReceived={setReceivingPix}
                  onWhatsApp={handleWhatsApp}
                />
              </div>
            ))}
          </div>

          {/* Rodapé: contagem e ver todos */}
          <div className="flex items-center justify-between gap-3 px-1 py-1 text-xs text-muted-foreground">
            <span>
              Exibindo {shown.length} de {plural(visible.length, "cobrança", "cobranças")}
              {hasFilters && ` (${total} no total)`}
            </span>
            {visible.length > shown.length ? (
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="flex items-center gap-1 text-[13px] font-semibold text-primary hover:underline"
              >
                Ver todos
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            ) : (
              hasFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-[13px] font-semibold text-primary hover:underline"
                >
                  Limpar filtros
                </button>
              )
            )}
          </div>
        </>
      )}

      {/* Cobrança no caixa */}
      <section className="flex flex-col gap-3 rounded-xl bg-surface-container/70 p-4 shadow-soft">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Store className="h-[18px] w-[18px]" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display text-base font-bold text-primary md:text-lg">
              Cobrança no Caixa
            </h2>
            <p className="truncate text-xs text-muted-foreground">
              Atalhos rápidos para agilizar a fila
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 lg:max-w-xl">
          <ShortcutButton
            icon={QrCode}
            title="Exibir QR Balcão"
            hint={shortcutHint || "Sem valor fixo"}
            onClick={handleCounterQr}
            disabled={!pixConfigured}
          />
          <ShortcutButton
            icon={keyCopied ? Check : Link2}
            title={keyCopied ? "Chave copiada!" : "Chave PIX"}
            hint={
              pixKey
                ? `Copiar chave ${pixService.getPixKeyType(pixKey)}`
                : shortcutHint || "Configure o PIX em Empresas"
            }
            onClick={handleCopyKey}
            disabled={!pixKey}
          />
        </div>
      </section>

      {/* Modais */}
      <PaymentDetailsModal
        open={!!detailsPix}
        onOpenChange={(open) => !open && setDetailsPix(null)}
        payment={detailsPix}
        onViewQRCode={handleViewQRCode}
        onMarkReceived={setReceivingPix}
        onWhatsApp={handleWhatsApp}
        onDelete={setPixToDelete}
      />

      <PixQrCodeModal
        open={!!qrPix}
        onOpenChange={(open) => !open && setQrPix(null)}
        pix={qrPix}
      />

      <PixReceivedModal
        open={!!receivingPix}
        onOpenChange={(open) => !open && setReceivingPix(null)}
        pix={receivingPix}
        onConfirm={handleConfirmReceived}
        loading={confirming}
      />

      <PixFormModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSave={handleCreatePix}
        loading={creating}
      />

      <AlertDialog open={!!pixToDelete} onOpenChange={(open) => !open && setPixToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cobrança</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a cobrança {pixToDelete && getPixTitle(pixToDelete)} de{" "}
              {formatCurrency(pixService.getAmount(pixToDelete))}
              {pixToDelete?.name ? ` (${pixToDelete.name})` : ""}?
              {pixToDelete && !pixService.isPaid(pixToDelete) &&
                " Se o cliente pagar esse QR Code depois, o pagamento não aparecerá nesta tela."}{" "}
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
